import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Wrench,
  ArrowLeft,
  Calendar as CalendarIcon,
  Clock,
  MapPin,
  ChevronLeft,
  ChevronRight,
  Phone
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import * as crmBackend from '../services/crmBackendService';

const TechSchedule = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('week'); // 'week' or 'day'
  const [jobs, setJobs] = useState([]);

  // Get week dates
  const getWeekDates = (date) => {
    const week = [];
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() - date.getDay()); // Start from Sunday

    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      week.push(day);
    }
    return week;
  };

  const weekDates = getWeekDates(currentDate);

  const currentWeekStart = weekDates[0];

  const loadWeekJobs = async (weekStart) => {
    const techUser = JSON.parse(localStorage.getItem('techUser') || '{}');
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    const startStr = weekStart.toISOString().split('T')[0];
    const endStr = weekEnd.toISOString().split('T')[0];
    try {
      const result = await crmBackend.getScheduledQuotations(startStr, endStr, techUser.email || null);
      setJobs(result.data || []);
    } catch {
      setJobs([]);
    }
  };

  useEffect(() => {
    loadWeekJobs(currentWeekStart);
  }, [currentWeekStart.toISOString().split('T')[0]]);

  const goToPreviousWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const goToNextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  const getJobsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return jobs.filter(job => job.custom_scheduled_date === dateStr);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-green-700 text-white sticky top-0 z-50 shadow-lg">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/tech/dashboard')}
              className="flex items-center space-x-2 hover:bg-green-600 px-3 py-2 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back</span>
            </button>
            <div className="flex items-center space-x-2">
              <CalendarIcon className="w-6 h-6" />
              <span className="font-bold text-lg">Schedule</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Week Navigation */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={goToPreviousWeek}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-6 h-6 text-gray-700" />
          </button>
          <div className="text-center">
            <p className="font-bold text-lg text-gray-900">
              {weekDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {weekDates[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </p>
            <p className="text-sm text-gray-600">Week View</p>
          </div>
          <button
            onClick={goToNextWeek}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronRight className="w-6 h-6 text-gray-700" />
          </button>
        </div>

        {/* Weekly Schedule */}
        <div className="space-y-4">
          {weekDates.map((date, index) => {
            const dayJobs = getJobsForDate(date);
            const isToday = date.toDateString() === new Date().toDateString();

            return (
              <div key={index}>
                {/* Day Header */}
                <div className={`flex items-center space-x-3 mb-2 pb-2 border-b-2 ${
                  isToday ? 'border-green-600' : 'border-gray-200'
                }`}>
                  <div className={`w-16 h-16 rounded-lg flex flex-col items-center justify-center ${
                    isToday ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700'
                  }`}>
                    <p className="text-xs font-medium">
                      {date.toLocaleDateString('en-US', { weekday: 'short' })}
                    </p>
                    <p className="text-2xl font-bold">{date.getDate()}</p>
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">
                      {date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
                    </p>
                    <p className="text-sm text-gray-600">{dayJobs.length} job{dayJobs.length !== 1 ? 's' : ''}</p>
                  </div>
                </div>

                {/* Jobs for this day */}
                {dayJobs.length === 0 ? (
                  <div className="ml-20 text-sm text-gray-500 italic py-2">
                    No jobs scheduled
                  </div>
                ) : (
                  <div className="ml-20 space-y-2">
                    {dayJobs.map(job => (
                      <Card
                        key={job.name}
                        className="bg-white hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => navigate(`/tech/job/${job.name}`)}
                      >
                        <CardContent className="p-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900">{job.party_name}</h4>
                              <p className="text-sm text-gray-600 mt-1">{job.service_type}</p>
                              <div className="flex items-center space-x-3 mt-2 text-sm text-gray-500">
                                <div className="flex items-center space-x-1">
                                  <Clock className="w-4 h-4" />
                                  <span>{job.custom_scheduled_time}</span>
                                </div>
                                <div className="flex items-center space-x-1">
                                  <MapPin className="w-3 h-3" />
                                  <span>{job.custom_service_address?.split(',')[1]?.trim()}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-col items-end space-y-2">
                              <Badge className={(job.custom_pipeline_status
                                ? crmBackend.pipelineStatusColor(job.custom_pipeline_status)
                                : crmBackend.getJobStatusColor(job.custom_job_status)) + ' border text-xs'}>
                                {crmBackend.pipelineStatusLabel(job)}
                              </Badge>
                              <a
                                href={`tel:${job.customer_phone}`}
                                onClick={(e) => e.stopPropagation()}
                                className="text-green-600"
                              >
                                <Phone className="w-4 h-4" />
                              </a>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Powered By Footer */}
      <div className="mt-8 py-6 border-t border-gray-200 text-center text-xs text-gray-500 bg-white">
        Powered by <span className="font-bold text-gray-900">Boatman Systems™</span>
      </div>
    </div>
  );
};

export default TechSchedule;
